/**
 * The flight dynamics model: everything assembled into one 120 Hz tick.
 *
 * Order of work each tick:
 *
 *   1. sample the atmosphere at the current altitude, and the wind at that altitude;
 *   2. form the velocity relative to the air mass, rotate it into body axes, derive α and β;
 *   3. advance the engine/propeller shaft and read off thrust, torque and slipstream;
 *   4. accumulate every aerodynamic panel's force and moment about the centre of gravity;
 *   5. add the propulsion force and its moments (thrust offset, torque reaction, P-factor);
 *   6. add the landing gear reaction;
 *   7. integrate the rigid body;
 *   8. derive the display state (IAS, VSI, load factor, …) and update the damage flags.
 *
 * ## Control conventions
 *
 * Pilot inputs are **commands**, normalised to −1…1, and positive is always the positive
 * aircraft response: `+pitch` raises the nose, `+roll` drops the right wing, `+yaw` swings the
 * nose right. The mapping from a command to a surface deflection, including which way the
 * surface physically moves, lives in the aircraft config's per-panel control gains. Nothing in
 * this file knows what kind of aeroplane it is flying (non-negotiable #8).
 *
 * Trim shifts the elevator's **zero point** rather than adding a parallel input, which is what
 * makes it relieve stick force: at the trimmed speed the pilot can let go and the aircraft
 * stays where it is.
 *
 * `updateFixed(dt)`: yes, at 120 Hz. Allocation-free after construction.
 */

import { Quaternion, Vector3 } from 'three';
import { RigidBody } from './rigidBody.js';
import {
  Atmosphere,
  WindModel,
  makeAtmosphereSample,
  trueToIndicatedAirspeed,
  type AtmosphereSample,
} from './atmosphere.js';
import { makeFlowContext, type AeroPanel, type FlowContext } from './aero/panel.js';
import { AeroAssembly } from './aero/assembly.js';
import { Curve1D } from './aero/tables.js';
import { makeGearOutput, type GearInputs, type GearOutput, type GroundContext } from './gear.js';
import { makeEulerAngles, quaternionToEuler, type EulerAngles } from './frames.js';
import {
  aileronTrimRangeDeg,
  rudderTrimRangeDeg,
  type LoadedAircraft,
  type RetractableGearConfig,
  type SpeedbrakeConfig,
} from '../aircraft/config.js';
import { FlyByWire, makeFlyByWireFeedback } from './fbw.js';
import { DEG2RAD, clamp, moveTowards } from '../core/math.js';
import { GRAVITY } from './atmosphere.js';
import { FULL_STORES, type StoresLoad } from '../aircraft/weapons/stores.js';

/** Pilot control commands, all normalised. */
export interface ControlInputs {
  /** Pitch command, −1 (nose down) … +1 (nose up). */
  pitch: number;
  /** Roll command, −1 (left) … +1 (right). */
  roll: number;
  /** Yaw command, −1 (left) … +1 (right). */
  yaw: number;
  /** Throttle, 0…1. */
  throttle: number;
  /** Elevator trim, −1 (nose down) … +1 (nose up). */
  pitchTrim: number;
  /**
   * Rudder trim, −1 (nose left) … +1 (nose right).
   *
   * Shifts the rudder's zero point, exactly as elevator trim shifts the elevator's — so at the
   * trimmed power setting the pilot's feet come off and the aeroplane keeps flying straight.
   */
  yawTrim: number;
  /**
   * Aileron trim, −1 (left wing down) … +1 (right wing down).
   *
   * Shifts the aileron's zero point, the same way the other two shift theirs — so a wing that is
   * standing heavy can be trimmed off rather than held off for the whole flight.
   */
  rollTrim: number;
  /**
   * Nosewheel steering command, −1…1. **Defaults to {@link yaw}.**
   *
   * On a real aeroplane the pedals do both jobs, so the default is the honest one and every
   * caller that does not care can ignore this field. What separates them is the auto-coordination
   * assist, which sets this to the *pilot's* pedal only and leaves its own contribution on the
   * rudder alone. Two reasons, and both matter:
   *
   *   - The assist's job is to cancel an *aerodynamic* moment. Steering is not that, and the
   *     assist's charter (see `input/coordination.ts`) is explicitly that it does not steer.
   *   - The nose tyre's side force is `μ·N·tanh(v_lat / 0.6 m/s)`, which saturates within about a
   *     degree of steer at taxi speed. Feeding a computed moment forward through a bang-bang
   *     element does not invert it — measured, it swung the nose *right* about as hard as the
   *     propeller was swinging it left.
   */
  steer?: number;
  /**
   * Ignition (magnetos). **Defaults to on.**
   *
   * Optional for the same reason {@link steer} is: every existing caller — the trim solver, the
   * validation gates, the perf harness — is describing an aeroplane whose engine is running, and
   * making them all say so would be noise. Only the shell's start/shutdown binding sets it.
   */
  ignition?: boolean;
  /** Starter motor, held. Defaults to off. */
  starter?: boolean;
  /** Index into the config's flap detents. */
  flapDetent: number;
  /** Left brake, 0…1. */
  brakeLeft: number;
  /** Right brake, 0…1. */
  brakeRight: number;
  /** Parking brake. */
  parkingBrake: boolean;
  /**
   * Gear lever: down. **Defaults to true.**
   *
   * Optional for the same reason {@link steer} and {@link ignition} are: two of the three
   * aircraft have their wheels welded down, every existing caller is describing one of them, and
   * an aeroplane whose gear cannot retract cannot raise it however this is set.
   */
  gearDown?: boolean;
  /** Speedbrake, 0…1. Defaults to closed; ignored by an aircraft that has none. */
  speedbrake?: number;
}

export function makeControlInputs(): ControlInputs {
  return {
    pitch: 0,
    roll: 0,
    yaw: 0,
    throttle: 0,
    pitchTrim: 0,
    yawTrim: 0,
    rollTrim: 0,
    flapDetent: 0,
    brakeLeft: 0,
    brakeRight: 0,
    parkingBrake: false,
  };
}

/** Damage and limit flags. Sim-grade flying, arcade-friendly failure. */
export interface DamageState {
  /** Exceeded the positive or negative g limit. */
  overstressed: boolean;
  /** Flaps extended above Vfe. */
  flapOverspeed: boolean;
  /** Above Vne. */
  overspeed: boolean;
  /** Hit the ground hard enough, or at an attitude the gear cannot absorb. */
  crashed: boolean;
  /** Peak load factor seen this flight [g]. */
  peakLoadFactor: number;
}

/** Everything the HUD, the audio and the cameras read. Reused; never retained. */
export interface FlightState {
  /** True airspeed [m/s]. */
  trueAirspeedMps: number;
  /** Indicated airspeed [m/s], with the aircraft's position error applied. */
  indicatedAirspeedMps: number;
  /** Calibrated (equivalent) airspeed [m/s]. */
  calibratedAirspeedMps: number;
  /** Ground speed [m/s]. */
  groundSpeedMps: number;
  /** Altitude above mean sea level [m]. */
  altitudeM: number;
  /** Height above the ground directly below [m]. */
  heightAboveGroundM: number;
  /** Rate of climb [m/s], positive up. */
  verticalSpeedMps: number;
  headingRad: number;
  pitchRad: number;
  rollRad: number;
  /** Angle of attack [rad]. */
  alphaRad: number;
  /** Sideslip angle [rad], positive nose-left of the relative wind. */
  betaRad: number;
  /** Load factor [g]. */
  loadFactor: number;
  /** Flight path angle [rad]. */
  flightPathAngleRad: number;
  rpm: number;
  /** Engine power actually produced [W]. */
  powerW: number;
  thrustN: number;
  /** Flap angle [deg]. */
  flapAngleDeg: number;
  /** Elevator trim setting, −1…1. */
  trim: number;
  /** Rudder trim setting, −1…1. */
  rudderTrim: number;
  /** Aileron trim setting, −1…1. */
  aileronTrim: number;
  /** Throttle lever position, 0…1 — what the pilot has set, not what the engine is doing. */
  throttle: number;
  onGround: boolean;
  /** Is the engine firing? False while shut down and while the starter is still cranking. */
  engineRunning: boolean;
  /** Is the starter motor turning the crank? */
  starterEngaged: boolean;
  /** Whole-aircraft lift coefficient. */
  liftCoefficient: number;
  /** Is any wing panel past its stall angle? */
  stallWarning: boolean;
  machNumber: number;
  /** Reheat, 0…1. Always zero without an afterburner. */
  afterburnerFraction: number;
  /** Fuel flow [kg/s]. Zero where it is not modelled. */
  fuelFlowKgPerS: number;
  /** Undercarriage position: 0 up and locked, 1 down and locked. */
  gearFraction: number;
  /** Speedbrake position, 0…1. */
  speedbrakeFraction: number;
}

function makeFlightState(): FlightState {
  return {
    trueAirspeedMps: 0,
    indicatedAirspeedMps: 0,
    calibratedAirspeedMps: 0,
    groundSpeedMps: 0,
    altitudeM: 0,
    heightAboveGroundM: 0,
    verticalSpeedMps: 0,
    headingRad: 0,
    pitchRad: 0,
    rollRad: 0,
    alphaRad: 0,
    betaRad: 0,
    loadFactor: 1,
    flightPathAngleRad: 0,
    rpm: 0,
    powerW: 0,
    thrustN: 0,
    flapAngleDeg: 0,
    trim: 0,
    rudderTrim: 0,
    aileronTrim: 0,
    throttle: 0,
    onGround: false,
    engineRunning: true,
    starterEngaged: false,
    liftCoefficient: 0,
    stallWarning: false,
    machNumber: 0,
    afterburnerFraction: 0,
    fuelFlowKgPerS: 0,
    gearFraction: 1,
    speedbrakeFraction: 0,
  };
}

/**
 * The propeller's standing yaw moment, and the rudder that would cancel it.
 *
 * Published every tick so the coordination assist can *feed forward* rather than wait for a
 * sideslip to develop — which is the whole difference between an assist that works on the take-off
 * roll and one that does not. On the ground there is no useful β to null (the wheels hold the
 * aeroplane's nose pointing where it is pointing, so the slip a pilot corrects to has not happened
 * yet), and at 10 kt there is no dynamic pressure for a feedback loop to work against. What there
 * *is*, from the instant the throttle moves, is a computable yaw moment.
 *
 * All moments are body-axis N·m, positive nose-right — so the propeller's contributions are
 * negative for a clockwise propeller and the rudder needed to cancel them is positive.
 */
export interface YawBalance {
  /** P-factor: the descending blade's thrust offset [N·m]. Straight from the propulsion model. */
  pFactorNm: number;
  /**
   * Slipstream swirl acting on the fin [N·m].
   *
   * Estimated rather than measured, because the panel model folds it into the fin's total force
   * and separating it out would mean evaluating the fin twice per tick. The estimate is the
   * linearisation of exactly what the panel does — a lateral flow perturbation is an alpha shift,
   * an alpha shift is `a·Δα` of lift coefficient, and the moment is that force on its arm — and
   * `coordination.test.ts` pins it against the model by differencing a run with the swirl gain
   * zeroed.
   */
  swirlNm: number;
  /**
   * The fin's own rigging incidence [N·m].
   *
   * Nose-*right* on a trainer, because the fin is offset at the factory to cancel exactly the
   * swirl above. It belongs in this sum for the same reason the other two do: it is a standing
   * moment the pilot did not command. Leaving it out was measurable — the feed-forward
   * over-corrected into a slow right drift on the take-off roll, because it was cancelling a
   * disturbance the airframe had already partly cancelled for it.
   */
  finRiggingNm: number;
  /** The three together: the yaw the pilot's right foot is there to cancel [N·m]. */
  disturbanceNm: number;
  /**
   * Yaw moment per unit of rudder command, at this instant [N·m].
   *
   * Positive: a positive command yaws the nose right. It is *not* a constant — it scales with the
   * dynamic pressure over the fin, which in the propwash is large before the aeroplane is moving
   * at all. That is why the rudder works at the start of the take-off roll, and why dividing by
   * it is the correct way to turn a moment into a pedal position.
   */
  rudderAuthorityNm: number;
}

function makeYawBalance(): YawBalance {
  return { pFactorNm: 0, swirlNm: 0, finRiggingNm: 0, disturbanceNm: 0, rudderAuthorityNm: 0 };
}

/** Below this airspeed the aerodynamic angles are noise, not information [m/s]. */
export const MIN_MEANINGFUL_AIRSPEED_MPS = 10;
/** Airspeed below which a stall warning is meaningless [m/s]. */
const MIN_AIRSPEED_FOR_STALL_WARNING = 15;

const windWorld = /* #__PURE__ */ new Vector3();
const relativeVelocityWorld = /* #__PURE__ */ new Vector3();
const relativeVelocityBody = /* #__PURE__ */ new Vector3();
const forceBody = /* #__PURE__ */ new Vector3();
const momentBody = /* #__PURE__ */ new Vector3();
const thrustForce = /* #__PURE__ */ new Vector3();
const propellerFlow = /* #__PURE__ */ new Vector3();
const scratchMoment = /* #__PURE__ */ new Vector3();
const scratchVector = /* #__PURE__ */ new Vector3();

export interface FdmOptions {
  atmosphere?: Atmosphere;
  wind?: WindModel;
  /** Ground elevation [m MSL]. Flat until terrain lands. */
  groundElevationM?: number;
}

export class FlightDynamicsModel {
  readonly aircraft: LoadedAircraft;
  readonly body = new RigidBody();
  readonly atmosphere: Atmosphere;
  readonly wind: WindModel | undefined;
  readonly state = makeFlightState();
  readonly damage: DamageState = {
    overstressed: false,
    flapOverspeed: false,
    overspeed: false,
    crashed: false,
    peakLoadFactor: 1,
  };

  /** Ground elevation under the aircraft [m MSL]. */
  groundElevationM: number;

  /**
   * The atmosphere at the aircraft, refreshed every tick.
   *
   * Public because the audio system needs the *local* speed of sound: the propeller's tip Mach
   * and the Doppler ratio are both divisions by it, and at 20 000 ft it is 5 % lower than at sea
   * level. Recovering it from `machNumber` instead would divide by zero on the ramp.
   */
  readonly atmosphereSample: AtmosphereSample = makeAtmosphereSample();
  private readonly flow: FlowContext = makeFlowContext();
  private readonly euler: EulerAngles = makeEulerAngles();
  private readonly gearInputs: GearInputs = { brakeLeft: 0, brakeRight: 0, steer: 0 };
  private gearOutput: GearOutput = makeGearOutput();

  /**
   * Propeller hub position, per instance rather than module-level: two aircraft in the same
   * process (which multiplayer means from day one) must not share it.
   */
  private readonly propellerPosition = new Vector3();

  /**
   * The ground context handed to the gear, built once. Rebuilding this object — and the two
   * frame-conversion closures inside it — every tick would allocate on the hot path, which is
   * exactly what non-negotiable #3 forbids.
   */
  private readonly groundContext: GroundContext;

  /** The panels, evaluated as a coupled set so the tail flies in the wing's downwash. */
  private readonly aero: AeroAssembly;

  /**
   * Pose at the start of the current tick, and the interpolated result.
   *
   * Rendering runs at the display rate and physics at 120 Hz, so the renderer must interpolate
   * between the last two physics states rather than draw the newest one (non-negotiable #2).
   * Keeping the previous pose here, rather than in the renderer, means every consumer —
   * camera, model, future replay — interpolates the same way.
   */
  private readonly previousPositionNed = new Vector3();
  private readonly previousAttitude = new Quaternion();
  private readonly sampledPositionNed = new Vector3();
  private readonly sampledAttitude = new Quaternion();
  /** The wrapper handed back by {@link samplePose}; reused, like the vectors in it. */
  private readonly sampledPose = {
    positionNed: this.sampledPositionNed,
    attitude: this.sampledAttitude,
    propAngleRad: 0,
  };
  private previousPropAngleRad = 0;
  private propAngleRad = 0;

  /** Current flap angle [deg], which chases the commanded detent at a finite rate. */
  private flapAngleDeg = 0;
  private readonly maxFlapDeg: number;
  /**
   * Undercarriage position: 0 up and locked, 1 down and locked.
   *
   * Starts down, because an aeroplane spawns on a runway. Pinned at 1 for an aircraft with no
   * retraction mechanism, so nothing downstream needs to know which kind it is looking at.
   */
  private gearFractionValue = 1;
  private speedbrakeFractionValue = 0;
  /**
   * What has been shot off, and what that does to the aeroplane. See `weapons/stores.ts`.
   *
   * Held as the *delta from a full load*, so an aircraft that has fired nothing — and every
   * aircraft that carries nothing — is exactly the aeroplane the validation gates were written
   * against. The centre-of-gravity offset it implies is recomputed here rather than stored, because
   * it depends on the current mass as well as the moment.
   */
  private stores: StoresLoad = FULL_STORES;
  /** Parasite drag area of a *full* weapons load [m²]. Resolved once; see the force accumulation. */
  private readonly fullStoresDragAreaM2: number;
  /** Centre of gravity relative to the body origin [m], moved by what has been expended. */
  private readonly cgOffset = new Vector3();
  /** Rudder trim authority [deg], resolved once from the config's value or the default. */
  private readonly rudderTrimRangeDeg: number;
  /** Aileron trim authority [deg], likewise. */
  private readonly aileronTrimRangeDeg: number;

  /**
   * The propeller's standing yaw and the rudder that cancels it. Rewritten every tick, never
   * reallocated. See {@link YawBalance}.
   */
  readonly yawBalance: YawBalance = makeYawBalance();

  /**
   * The flight control laws, or null for an aeroplane whose stick moves the surfaces.
   *
   * Public because the HUD wants to show what the law is doing — whether the α limiter is in
   * charge, what g is being commanded — and because the gates assert against its diagnostics.
   */
  readonly fbw: FlyByWire | null;
  private readonly fbwFeedback = makeFlyByWireFeedback();
  /** The law's output, preallocated: a control law on the 120 Hz tick must not allocate. */
  private readonly fbwCommands = makeControlInputs();

  /** Transonic drag rise, or null for an aircraft that never gets near it. */
  private readonly waveDragCurve: Curve1D | null;
  /** Mach tuck: the nose-down moment from the shock moving the centre of pressure aft. */
  private readonly machTuckCurve: Curve1D | null;
  /** What fraction of its control authority the shock has left the aeroplane. */
  private readonly controlEffectivenessCurve: Curve1D | null;
  private readonly retractableGear: RetractableGearConfig | null;
  /**
   * How far the lowest wheel hangs below the centre of gravity [m], gear extended.
   *
   * Read from the aircraft's own wheel positions rather than declared, so it cannot drift away
   * from the geometry the ground reaction is computed against. Used by the terrain-impact test:
   * the CG of an aeroplane standing on its wheels is legitimately this far above the ground, and
   * the same aeroplane with the gear up is not.
   */
  private readonly gearGroundClearanceM: number;
  private readonly speedbrake: SpeedbrakeConfig | null;
  private readonly speedbrakePosition = new Vector3();

  /**
   * The fin, reduced to the four numbers the yaw balance needs.
   *
   * Resolved once from the panels rather than written down again: the arm, the area, the
   * lift-curve slope the table actually produces, how much of the fin the propwash washes, and
   * the rudder's effectiveness. Null for an aircraft with no vertical surface carrying a rudder,
   * in which case the balance reports zeros and the assist falls back to pure feedback.
   */
  private readonly fin: {
    armM: number;
    areaM2: number;
    liftSlopePerRad: number;
    slipstreamFraction: number;
    rudderGain: number;
    incidenceRad: number;
  } | null;

  constructor(aircraft: LoadedAircraft, options: FdmOptions = {}) {
    this.aircraft = aircraft;
    this.body.setMassProperties(aircraft.massProperties);
    this.atmosphere = options.atmosphere ?? new Atmosphere();
    this.wind = options.wind;
    this.groundElevationM = options.groundElevationM ?? 0;
    const detents = aircraft.config.controls.flapDetentsDeg;
    this.maxFlapDeg = detents[detents.length - 1];
    this.rudderTrimRangeDeg = rudderTrimRangeDeg(aircraft.config.controls);
    this.aileronTrimRangeDeg = aileronTrimRangeDeg(aircraft.config.controls);

    const hub = aircraft.propellerPositionM;
    this.propellerPosition.set(hub[0], hub[1], hub[2]);

    this.fbw = aircraft.config.flyByWire === undefined ? null : new FlyByWire(aircraft.config.flyByWire);
    const compressibility = aircraft.config.compressibility;
    this.waveDragCurve =
      compressibility === undefined
        ? null
        : new Curve1D(compressibility.machNumbers, compressibility.waveDragCoefficient, 'wave drag');
    this.machTuckCurve =
      compressibility?.pitchingMomentCoefficient === undefined
        ? null
        : new Curve1D(
            compressibility.machNumbers,
            compressibility.pitchingMomentCoefficient,
            'mach tuck',
          );
    this.controlEffectivenessCurve =
      compressibility?.controlEffectiveness === undefined
        ? null
        : new Curve1D(
            compressibility.machNumbers,
            compressibility.controlEffectiveness,
            'control effectiveness',
          );
    this.retractableGear = aircraft.config.gear.retractable ?? null;
    let lowestWheelM = 0;
    // Body axes are x forward, y right, **z down**, so a wheel's z is already its drop below
    // the CG and the lowest wheel is the largest of them.
    for (const wheel of aircraft.config.gear.wheels) {
      lowestWheelM = Math.max(lowestWheelM, wheel.positionM[2]);
    }
    this.gearGroundClearanceM = lowestWheelM;
    const missile = aircraft.config.weapons?.missile;
    this.fullStoresDragAreaM2 =
      missile === undefined ? 0 : missile.count * (missile.railDragAreaM2 ?? 0);
    this.speedbrake = aircraft.config.speedbrake ?? null;
    if (this.speedbrake !== null) {
      const at = this.speedbrake.positionM;
      this.speedbrakePosition.set(at[0], at[1], at[2]);
    }

    this.aero = new AeroAssembly(
      aircraft.panels,
      aircraft.config.geometry.wingAreaM2,
      aircraft.config.geometry.wingSpanM,
    );

    // The rudder-carrying vertical panel, if there is one. `rudderGain` rather than the name, so
    // this is data-driven: whatever the config calls its fin, the fin is the vertical surface the
    // rudder acts on.
    let fin: AeroPanel | null = null;
    for (const panel of this.aero.panels) {
      if (panel.kind !== 'vertical' || panel.rudderGain === 0) continue;
      if (fin === null || Math.abs(panel.rudderGain) > Math.abs(fin.rudderGain)) fin = panel;
    }
    this.fin =
      fin === null
        ? null
        : {
            // Body x is forward, so a tail surface sits at negative x and the arm is negative —
            // which is exactly right: `N_z = x·F_y`, and a fin force to starboard yaws the nose
            // left. Keeping the sign rather than taking a magnitude is what makes a canard work.
            armM: fin.position.x,
            areaM2: fin.areaM2,
            liftSlopePerRad: fin.liftSlopePerRad,
            slipstreamFraction: fin.slipstreamFraction,
            rudderGain: fin.rudderGain,
            /*
             * The fin's rigging incidence, recovered from the axes the panel built.
             *
             * Not a detail — it is the *point* of the fin, and leaving it out is what made the
             * first version of this feed-forward over-correct into a slow right drift. A trainer's
             * fin is offset a couple of degrees at the factory precisely to cancel slipstream
             * swirl at cruise, so its standing moment is nose-right and scales with the same
             * dynamic pressure the swirl does. Estimating the disturbance without it counts the
             * swirl and not the thing built to oppose it.
             *
             * For a vertical panel `chordAxis = (cos i, sin i, 0)`, so this reads the angle back
             * out rather than needing the config threaded through — which also means it is right
             * for an aircraft whose fin is rigged the other way, or not at all.
             */
            incidenceRad: Math.atan2(fin.chordAxis.y, fin.chordAxis.x),
          };

    const body = this.body;
    this.groundContext = {
      positionNed: body.positionNed,
      velocityNed: body.velocityNed,
      angularVelocityBody: body.angularVelocityBody,
      bodyToWorld: body.bodyToWorld.bind(body),
      worldToBody: body.worldToBody.bind(body),
      groundElevationM: this.groundElevationM,
      massKg: aircraft.massProperties.massKg,
    };
  }

  /**
   * Tell the flight model what is still hanging off the aeroplane.
   *
   * Called by the weapon system when a count changes, not every tick: expending a store is a
   * discrete event, and recomputing the inertia tensor's inverse a hundred and twenty times a
   * second for a number that changes a few times a sortie is work for nothing.
   *
   * Three effects, and the third is the one that is felt:
   *
   *   - **Mass** comes off, so the same thrust accelerates harder and the same lift pulls more g.
   *   - **Inertia** comes off by the parallel-axis term. Four wingtip rounds are 340 kg at 4.6 m,
   *     which is about 7 200 kg·m² of roll inertia against an airframe's 9 496 — so a Vandal that
   *     has fired everything rolls appreciably faster, which is exactly what pilots report.
   *   - **The centre of gravity moves**, and a centre of gravity displaced from the body origin
   *     turns weight into a pitching moment. Firing the gun dry takes 51 kg off a station 2.1 m
   *     forward, which trims the aeroplane nose-up by a small but real amount.
   */
  setStores(stores: StoresLoad): void {
    this.stores = stores;
    const base = this.aircraft.massProperties;
    const massKg = Math.max(1, base.massKg - stores.expendedKg);
    this.body.setMassProperties({
      massKg,
      // Floors, because a config could in principle declare stores heavier than the airframe's
      // own inertia about an axis, and a non-positive-definite tensor throws.
      ixx: Math.max(1, base.ixx - stores.expendedIxx),
      iyy: Math.max(1, base.iyy - stores.expendedIyy),
      izz: Math.max(1, base.izz - stores.expendedIzz),
      ixz: base.ixz - stores.expendedIxz,
    });
    /*
     * The centre of gravity moves *opposite* to the mass that left.
     *
     * Removing mass `m` from a point `r` shifts the centre of gravity of what remains by
     * `−m·r / (M − m)`: take weight off the nose and the balance point goes aft. The moment stored
     * in `expendedMomentKgM` is `Σ m·r` for what has gone, so this is that divided by the new mass
     * and negated.
     */
    const moment = stores.expendedMomentKgM;
    this.cgOffset.set(-moment[0] / massKg, -moment[1] / massKg, -moment[2] / massKg);
  }

  /** The current centre of gravity relative to the body origin, body axes [m]. */
  get centreOfGravityOffsetM(): Vector3 {
    return this.cgOffset;
  }

  /** Height of the CG above the ground when sitting on the gear with the oleos static [m]. */
  get staticGroundClearanceM(): number {
    const weightPerWheel =
      (this.aircraft.massProperties.massKg * GRAVITY) / this.aircraft.config.gear.wheels.length;
    let lowest = 0;
    let compression = 0;
    for (const wheel of this.aircraft.config.gear.wheels) {
      if (wheel.positionM[2] > lowest) {
        lowest = wheel.positionM[2];
        compression = weightPerWheel / wheel.springRateNPerM;
      }
    }
    return lowest - compression;
  }

  /**
   * Advance one 120 Hz tick.
   * @returns the derived {@link FlightState}, reused between calls.
   */
  /**
   * One 120 Hz tick, integrated in one or more sub-steps.
   *
   * ## Why this is not simply one step
   *
   * A pilot reported the aeroplane jittering above about 480 KIAS, in every camera view. It was
   * not the renderer — it was **the aeroplane genuinely oscillating**, and the measurement is
   * unambiguous. Flown hands-off in steady level flight at 4,134 ft, sweeping airspeed:
   *
   * ```
   *   KIAS   g swing   pitch swing   peak pitch rate
   *    440    0.040       0.23°          0.08°/s
   *    460    0.037       0.21°          0.07°/s
   *    480    5.85        2.33°         36.0°/s     <-- 160x, with the stick centred
   *    580    7.32        5.33°         53.9°/s
   * ```
   *
   * At ~7.6 Hz, far too fast for any airframe mode — short period is 1–3 Hz. And it is **not a
   * control law**: halving the step removes it entirely (g swing 5.85 at 120 Hz, 0.0335 at 240,
   * 480 and 960). A real limit cycle persists as the step shrinks; a numerical instability
   * vanishes, which is what this does.
   *
   * The mechanism is ordinary. Aerodynamic rate damping is `ω̇ = −k·ω` with `k` proportional to
   * dynamic pressure, and the damping moment is evaluated **once per step, from the old rates** —
   * `RigidBody.step`'s implicit-midpoint iteration makes the *gyroscopic* term implicit but takes
   * `momentBody` as a frozen input, and the aero cannot be re-evaluated from inside it because
   * damping here is emergent from the component buildup rather than a coefficient. An explicit
   * `ω̇ = −k·ω` is stable only while `k·dt < 2`, so there is a speed at which 120 Hz crosses it —
   * and for this airframe that speed is 480 KIAS, inside an envelope that runs to 800.
   *
   * Sub-stepping restores `k·dt < 2` by shrinking `dt`. It changes no coefficient and no law: the
   * same model is simply integrated more accurately where it needs to be, which is why the
   * validation gates are unmoved by it.
   *
   * The history the renderer interpolates from is captured **here, once**, before any sub-step.
   * Capturing it inside would leave `previousPositionNed` at a mid-tick point while the loop's
   * `alpha` still spans the whole tick, and the renderer would extrapolate past the aeroplane.
   */
  updateFixed(pilotInputs: ControlInputs, dtSeconds: number): FlightState {
    // Snapshot the pose the renderer will interpolate from — before the first sub-step, so the
    // interval it describes is exactly the tick the scheduler's alpha is measured against.
    this.previousPositionNed.copy(this.body.positionNed);
    this.previousAttitude.copy(this.body.attitude);
    this.previousPropAngleRad = this.propAngleRad;

    /*
     * A wreck is not an aeroplane, and until now it went on being one.
     *
     * `crashed` has been set correctly since M1 and did nothing but change a caption: the flight
     * model kept integrating, so an aircraft that hit a hillside carried on through the rock at
     * 500 kt, under power, with the pilot still able to fly it. CLAUDE.md asks for "terrain impact
     * → crash state with instant respawn UI", and the state was there — the *stop* was not.
     *
     * Everything is held exactly where the impact left it, including the attitude, so the
     * wreckage lies at the angle it arrived at. The interpolation history above has already been
     * updated to the same pose, which is what stops the renderer from smearing between a moving
     * aeroplane and a stopped one on the frame the crash happens.
     */
    if (this.damage.crashed) return this.settleWreck();

    /*
     * The control law runs **once per tick, at the tick rate** — not once per sub-step.
     *
     * A flight control computer samples at its own fixed rate; how finely the airframe underneath
     * it is integrated is not its business, and a real one does not speed up because the simulator
     * chose a smaller step. Running it per sub-step also *changed the aeroplane*: each call saw
     * fresher feedback, so the g-command law backed off early and the fighter's corner-speed gate
     * fell from 8.97 g to 8.50 against a required 9. The validation table caught it, which is what
     * it is for.
     *
     * So the controller is hoisted here and its output — what the *surfaces* are being asked for —
     * is held constant across the sub-steps, which is exactly what a zero-order hold does in the
     * real aircraft.
     */
    let inputs = pilotInputs;
    if (this.fbw !== null) {
      const feedback = this.fbwFeedback;
      feedback.loadFactor = this.state.loadFactor;
      feedback.alphaRad = this.state.alphaRad;
      feedback.betaRad = this.state.betaRad;
      feedback.rollRateRadPerS = this.body.angularVelocityBody.x;
      feedback.pitchRateRadPerS = this.body.angularVelocityBody.y;
      feedback.yawRateRadPerS = this.body.angularVelocityBody.z;
      feedback.trueAirspeedMps = this.state.trueAirspeedMps;
      feedback.indicatedAirspeedMps = this.state.indicatedAirspeedMps;
      feedback.onGround = this.gearOutput.onGround;
      inputs = this.fbw.updateFixed(pilotInputs, feedback, dtSeconds, this.fbwCommands);
    }

    return this.integrateStep(inputs, dtSeconds);
  }



  /**
   * Stop dead, and stay stopped. Called every tick once {@link DamageState.crashed} is set.
   *
   * Only the motion is zeroed. Position, attitude, flap and gear positions are left untouched:
   * a crash is not a reset, and the pose is what the explosion and the wreck are drawn at. `R`
   * calls {@link reset}, which is what clears the flag and puts the aeroplane back.
   */
  private settleWreck(): FlightState {
    const body = this.body;
    body.velocityNed.set(0, 0, 0);
    body.angularVelocityBody.set(0, 0, 0);

    const state = this.state;
    state.trueAirspeedMps = 0;
    state.indicatedAirspeedMps = 0;
    state.calibratedAirspeedMps = 0;
    state.groundSpeedMps = 0;
    state.verticalSpeedMps = 0;
    state.machNumber = 0;
    state.loadFactor = 0;
    state.alphaRad = 0;
    state.betaRad = 0;
    state.flightPathAngleRad = 0;
    state.liftCoefficient = 0;
    state.stallWarning = false;
    // The engine stops too — otherwise the audio graph, which tracks rpm and reheat, keeps a
    // turbine spooled up inside a smoking hole.
    state.rpm = 0;
    state.powerW = 0;
    state.thrustN = 0;
    state.throttle = 0;
    state.afterburnerFraction = 0;
    state.fuelFlowKgPerS = 0;
    state.engineRunning = false;
    state.starterEngaged = false;
    state.onGround = true;
    return state;
  }

  /** One integration step, once per 120 Hz tick. */
  private integrateStep(pilotInputs: ControlInputs, dtSeconds: number): FlightState {
    const aircraft = this.aircraft;
    const config = aircraft.config;
    const body = this.body;

    /*
     * From here down, `inputs` is what the *surfaces* are being asked for. On a direct aeroplane
     * that is the pilot's stick unchanged; on the fighter it is already the output of the
     * g-command law, which {@link updateFixed} ran before calling this and which decided the
     * stick meant something else entirely.
     */
    const inputs = pilotInputs;

    // --- flap actuator ----------------------------------------------------------------
    const detents = config.controls.flapDetentsDeg;
    const commandedDeg = detents[clamp(Math.round(inputs.flapDetent), 0, detents.length - 1)];
    const flapRateDegPerS = this.maxFlapDeg / Math.max(0.1, config.controls.flapTravelTimeS);
    this.flapAngleDeg = moveTowards(this.flapAngleDeg, commandedDeg, flapRateDegPerS * dtSeconds);
    const flapFraction = this.maxFlapDeg > 0 ? this.flapAngleDeg / this.maxFlapDeg : 0;

    // --- undercarriage and airbrake actuators -------------------------------------------
    // Both are first-order travel toward a commanded position, and both are pinned when the
    // aircraft has no such device — so the rest of the tick never asks whether it does.
    if (this.retractableGear !== null) {
      this.gearFractionValue = moveTowards(
        this.gearFractionValue,
        (inputs.gearDown ?? true) ? 1 : 0,
        dtSeconds / Math.max(0.1, this.retractableGear.travelTimeS),
      );
    }
    if (this.speedbrake !== null) {
      this.speedbrakeFractionValue = moveTowards(
        this.speedbrakeFractionValue,
        clamp(inputs.speedbrake ?? 0, 0, 1),
        dtSeconds / Math.max(0.1, this.speedbrake.travelTimeS),
      );
    }

    // --- atmosphere and relative wind ---------------------------------------------------
    const altitude = body.altitudeM;
    const atmosphere = this.atmosphere.sample(altitude, this.atmosphereSample);

    relativeVelocityWorld.copy(body.velocityNed);
    if (this.wind !== undefined) {
      this.wind.updateFixed(dtSeconds);
      this.wind.sample(altitude, windWorld);
      relativeVelocityWorld.sub(windWorld);
    }
    body.worldToBody(relativeVelocityWorld, relativeVelocityBody);

    const trueAirspeed = relativeVelocityBody.length();
    const alphaRad = Math.atan2(relativeVelocityBody.z, relativeVelocityBody.x);
    const betaRad = trueAirspeed > 0.1 ? Math.asin(clamp(relativeVelocityBody.y / trueAirspeed, -1, 1)) : 0;

    // --- propulsion ----------------------------------------------------------------------
    // Flow at the propeller disc includes the rotational velocity of that point.
    scratchVector.copy(body.angularVelocityBody).cross(this.propellerPosition);
    propellerFlow.copy(relativeVelocityBody).add(scratchVector);
    const propAlpha = Math.atan2(propellerFlow.z, propellerFlow.x);
    const propSpeed = propellerFlow.length();
    const propBeta = propSpeed > 0.1 ? Math.asin(clamp(propellerFlow.y / propSpeed, -1, 1)) : 0;

    // The powerplant's two switches. Written rather than passed, so that a six-argument
    // signature does not become an eight-argument one for two booleans; see `propulsion.ts`.
    aircraft.propulsion.ignitionOn = inputs.ignition ?? true;
    aircraft.propulsion.starterEngaged = inputs.starter ?? false;

    const propulsion = aircraft.propulsion.updateFixed(
      inputs.throttle,
      propellerFlow.x,
      propAlpha,
      propBeta,
      atmosphere,
      dtSeconds,
    );

    // --- aerodynamic panels ---------------------------------------------------------------
    const flow = this.flow;
    flow.densityKgPerM3 = atmosphere.densityKgPerM3;
    flow.velocityBody.copy(relativeVelocityBody);
    flow.angularVelocityBody.copy(body.angularVelocityBody);

    /*
     * How much of the control surfaces the shock has left the pilot.
     *
     * Applied to the *deflection* rather than to the resulting moment, which is the physically
     * honest place for it: a shocked section does not respond to the surface moving, so the
     * aeroplane behaves as though the pilot had moved it less far. Everything downstream — the
     * panel's own stall, the trim that shares the same range, the fly-by-wire watching the
     * response it is getting — then follows for free, and the FBW compensating by demanding more
     * surface is exactly what the real control law does.
     *
     * `machNumber` is computed here rather than further down, where the wave drag uses it,
     * because the deflections are assembled before the panels run.
     */
    const machNumber = trueAirspeed / atmosphere.speedOfSoundMps;
    const controlAuthority =
      this.controlEffectivenessCurve === null ? 1 : this.controlEffectivenessCurve.at(machNumber);

    // Commands → surface deflections. Trim shifts the elevator's zero point.
    const elevatorDeg =
      clamp(inputs.pitch, -1, 1) * config.controls.elevatorRangeDeg +
      clamp(inputs.pitchTrim, -1, 1) * config.controls.trimRangeDeg;
    flow.elevatorRad =
      -clamp(elevatorDeg, -config.controls.elevatorRangeDeg * 1.6, config.controls.elevatorRangeDeg * 1.6) *
      DEG2RAD *
      controlAuthority;
    // Aileron trim shifts the aileron's zero point, exactly as the other two trims do.
    const aileronDeg =
      clamp(inputs.roll, -1, 1) * config.controls.aileronRangeDeg +
      clamp(inputs.rollTrim, -1, 1) * this.aileronTrimRangeDeg;
    flow.aileronRad =
      clamp(aileronDeg, -config.controls.aileronRangeDeg * 1.6, config.controls.aileronRangeDeg * 1.6) *
      DEG2RAD *
      controlAuthority;
    // Rudder trim shifts the rudder's zero point, the same way elevator trim shifts the
    // elevator's: at the trimmed condition the pilot's feet come off and it still flies straight.
    const rudderDeg =
      clamp(inputs.yaw, -1, 1) * config.controls.rudderRangeDeg +
      clamp(inputs.yawTrim, -1, 1) * this.rudderTrimRangeDeg;
    flow.rudderRad =
      clamp(rudderDeg, -config.controls.rudderRangeDeg * 1.6, config.controls.rudderRangeDeg * 1.6) *
      DEG2RAD *
      controlAuthority;
    flow.flapFraction = flapFraction;
    flow.heightAboveGroundM = Math.max(0, altitude - this.groundElevationM);
    flow.slipstreamAxialMps = propulsion.slipstreamAxialMps;
    flow.slipstreamSwirlYMps = propulsion.slipstreamSwirlYMps;
    flow.slipstreamSwirlZMps = 0;

    forceBody.set(0, 0, 0);
    momentBody.set(0, 0, 0);

    const aeroTotals = this.aero.accumulate(flow, forceBody, momentBody);
    // A wing panel is trivially "past its stall angle" when the aeroplane is parked in a
    // tailwind, which is true and useless. A stall warning vane needs airflow to mean
    // anything, so the warning is gated on having enough of it to fly.
    const stallWarning = aeroTotals.stallWarning && trueAirspeed > MIN_AIRSPEED_FOR_STALL_WARNING;
    const totalLiftN = aeroTotals.totalLiftN;

    // --- propulsion forces and the left-turning tendencies -------------------------------
    thrustForce.set(propulsion.thrustN, 0, 0);
    forceBody.add(thrustForce);
    scratchMoment.copy(this.propellerPosition).cross(thrustForce);
    momentBody.add(scratchMoment);
    momentBody.x += propulsion.rollMomentNm;
    momentBody.y += propulsion.pitchMomentNm;
    momentBody.z += propulsion.yawMomentNm;

    // --- what the pilot's right foot is for -----------------------------------------------
    this.updateYawBalance(
      propulsion.slipstreamAxialMps,
      propulsion.slipstreamSwirlYMps,
      propulsion.yawMomentNm,
      relativeVelocityBody.x,
      atmosphere.densityKgPerM3,
      config.controls.rudderRangeDeg,
    );

    // --- parasite drag from things hanging in the airstream ---------------------------------
    /*
     * The gear and the airbrake, as drag areas rather than as panels.
     *
     * Both are flat plates normal to the flow whose lift is nil by construction, so a full
     * −180…180° table would be ceremony around one number. What they do have is a line of action:
     * the drag opposes the *relative wind*, not the body x axis, so it is applied along the
     * velocity vector — which is what makes an open airbrake pitch the nose at high α rather than
     * only slowing the aeroplane down.
     */
    /*
     * Wave drag joins them, as an area rather than as a coefficient.
     *
     * `ΔCD₀·S_ref` has the units of the other two, so the transonic rise, the undercarriage and
     * the airbrake are one force along one line of action instead of three. See
     * `CompressibilityConfig` for why this is a whole-aircraft term and not a per-panel one.
     */
    const waveDragArea =
      this.waveDragCurve === null
        ? 0
        : this.waveDragCurve.at(machNumber) * config.geometry.wingAreaM2;
    /*
     * Mach tuck — the nose-down moment the drag rise arrives with.
     *
     * Same reasoning as the wave drag directly above, and the same reason it is a whole-aircraft
     * term: the shock does not sit on a panel, it sits on the *aeroplane*, and the centre of
     * pressure it moves is the whole airframe's. Dimensionally it is the textbook
     * `Cm · q̄ · S · c̄`, referenced to the same wing area the drag increment is, so the two curves
     * describe one flow condition in one set of units.
     *
     * Applied about body y, positive nose-up, so a negative coefficient pitches down — which is
     * the only direction tuck goes, and which the config validator enforces rather than trusting.
     *
     * What makes it interesting to fly rather than merely a number: the *elevator* is losing
     * authority over exactly the same Mach range, so the pilot is asked for more nose-up trim at
     * the moment the surface is least able to supply it. That is the transonic region as it was
     * actually experienced, and neither half of it means much without the other.
     */
    if (this.machTuckCurve !== null && trueAirspeed > 1) {
      const dynamicPressurePa = 0.5 * atmosphere.densityKgPerM3 * trueAirspeed * trueAirspeed;
      momentBody.y +=
        this.machTuckCurve.at(machNumber) *
        dynamicPressurePa *
        config.geometry.wingAreaM2 *
        config.geometry.meanChordM;
    }

    /*
     * Stores drag is the *full* load's, less whatever has been fired off.
     *
     * Expressed as a subtraction for the same reason the mass is: `maxTakeoffKg` and every
     * published performance figure describe the aeroplane as loaded, so a full jet has to fly
     * exactly as it did before this term existed, and shooting the rails empty has to give
     * something back. See `weapons/stores.ts`.
     */
    const storesDragArea = Math.max(0, this.fullStoresDragAreaM2 - this.stores.expendedDragAreaM2);
    const dragArea =
      waveDragArea +
      storesDragArea +
      (this.retractableGear === null ? 0 : this.retractableGear.dragAreaM2 * this.gearFractionValue) +
      (this.speedbrake === null ? 0 : this.speedbrake.dragAreaM2 * this.speedbrakeFractionValue);
    if (dragArea > 0 && trueAirspeed > 1) {
      const dragN = 0.5 * atmosphere.densityKgPerM3 * trueAirspeed * trueAirspeed * dragArea;
      const scale = -dragN / trueAirspeed;
      scratchVector.copy(relativeVelocityBody).multiplyScalar(scale);
      forceBody.add(scratchVector);
      // The boards are aft and high, so their drag also pitches. The gear's own arm is small
      // enough to ignore beside the wheels' own contact forces.
      if (this.speedbrake !== null && this.speedbrakeFractionValue > 0) {
        const share =
          (this.speedbrake.dragAreaM2 * this.speedbrakeFractionValue) / Math.max(1e-9, dragArea);
        scratchVector.multiplyScalar(share);
        scratchMoment.copy(this.speedbrakePosition).cross(scratchVector);
        momentBody.add(scratchMoment);
      }
    }

    /*
     * A centre of gravity that is not at the body origin turns weight into a pitching moment.
     *
     * The rigid body integrates about its origin and adds gravity as an acceleration, which is
     * exactly right while the origin *is* the centre of gravity — and it stops being right the
     * moment a store leaves. Firing the gun dry takes 51 kg off a point 2.1 m ahead of the datum,
     * which moves the balance point about 9 mm aft on a 12-tonne aeroplane; the aerodynamic
     * moments are still taken about the datum, so the difference has to appear as the moment that
     * the aeroplane's own weight now makes about it.
     *
     * `r × W`, with the weight resolved into body axes. Skipped entirely while the offset is zero,
     * which is every aircraft that carries nothing and every armed one that has not fired.
     */
    if (this.cgOffset.lengthSq() > 0) {
      scratchVector.set(0, 0, this.body.massKg * GRAVITY); // NED: +z is down
      this.body.worldToBody(scratchVector, scratchVector);
      scratchMoment.copy(this.cgOffset).cross(scratchVector);
      momentBody.add(scratchMoment);
    }

    // --- landing gear ----------------------------------------------------------------------
    const parking = inputs.parkingBrake ? 1 : 0;
    this.gearInputs.brakeLeft = Math.max(clamp(inputs.brakeLeft, 0, 1), parking);
    this.gearInputs.brakeRight = Math.max(clamp(inputs.brakeRight, 0, 1), parking);
    this.gearInputs.steer = clamp(inputs.steer ?? inputs.yaw, -1, 1);

    this.groundContext.groundElevationM = this.groundElevationM;
    /*
     * The wheels only touch anything when they are down and locked.
     *
     * Partway is *in the well*, not partway to the runway: an undercarriage leg is either
     * carrying load or it is folded, and there is no useful middle. A gear-up arrival therefore
     * produces no contact force at all and is caught by the terrain-impact check in
     * `updateDamage`, which is the honest outcome — the aeroplane hits the ground.
     */
    this.gearOutput =
      this.gearFractionValue >= 0.999
        ? aircraft.gear.accumulate(
            this.groundContext,
            this.gearInputs,
            dtSeconds,
            forceBody,
            momentBody,
          )
        : aircraft.gear.retracted();

    // --- integrate ---------------------------------------------------------------------------
    body.step(forceBody, momentBody, dtSeconds);

    // --- derived state -----------------------------------------------------------------------
    quaternionToEuler(body.attitude, this.euler);
    const state = this.state;
    state.trueAirspeedMps = trueAirspeed;
    state.calibratedAirspeedMps = trueToIndicatedAirspeed(trueAirspeed, atmosphere.densityRatio);

    const dynamicPressure = 0.5 * atmosphere.densityKgPerM3 * trueAirspeed * trueAirspeed;
    const liftCoefficient =
      dynamicPressure > 1 ? totalLiftN / (dynamicPressure * config.geometry.wingAreaM2) : 0;
    state.liftCoefficient = liftCoefficient;

    // Position error: the static source reads increasingly low as alpha rises, which is why
    // the POH lists KIAS and KCAS separately. See AirspeedCalibration.
    const calibration = aircraft.airspeedCalibration;
    const positionError =
      (calibration.perCl + calibration.perClWithFlap * flapFraction) * Math.max(0, liftCoefficient);
    state.indicatedAirspeedMps = Math.max(0, state.calibratedAirspeedMps - positionError);

    state.groundSpeedMps = body.groundSpeedMps;
    state.altitudeM = body.altitudeM;
    state.heightAboveGroundM = Math.max(0, body.altitudeM - this.groundElevationM);
    state.verticalSpeedMps = -body.velocityNed.z;
    state.headingRad = this.euler.headingRad;
    state.pitchRad = this.euler.pitchRad;
    state.rollRad = this.euler.rollRad;
    state.alphaRad = alphaRad;
    state.betaRad = betaRad;
    state.loadFactor = body.loadFactor;
    state.flightPathAngleRad =
      body.groundSpeedMps > 0.5 ? Math.atan2(-body.velocityNed.z, body.groundSpeedMps) : 0;
    state.rpm = propulsion.rpm;
    state.powerW = propulsion.powerW;
    state.thrustN = propulsion.thrustN;
    state.flapAngleDeg = this.flapAngleDeg;
    state.trim = inputs.pitchTrim;
    state.rudderTrim = inputs.yawTrim;
    state.aileronTrim = inputs.rollTrim;
    state.throttle = clamp(inputs.throttle, 0, 1);
    state.onGround = this.gearOutput.onGround;
    state.engineRunning = aircraft.propulsion.running;
    state.starterEngaged = aircraft.propulsion.starterEngaged;
    state.stallWarning = stallWarning;
    state.machNumber = machNumber;
    state.afterburnerFraction = propulsion.afterburnerFraction;
    state.fuelFlowKgPerS = propulsion.fuelFlowKgPerS;
    state.gearFraction = this.gearFractionValue;
    state.speedbrakeFraction = this.speedbrakeFractionValue;

    // The propeller angle is visual only, but it still has to be interpolated or the disc
    // strobes at the display rate.
    this.propAngleRad = (this.propAngleRad + (propulsion.rpm / 60) * Math.PI * 2 * dtSeconds) % (Math.PI * 2);

    this.updateDamage(state, config.limits);
    return state;
  }

  /**
   * Work out the standing yaw the propeller is putting on the airframe, and what it costs in
   * rudder. Allocation-free; writes {@link yawBalance} in place.
   *
   * The fin is a lifting surface and the two propeller effects reach it as flow perturbations, so
   * both terms are the same linearisation:
   *
   *     Δα_fin = −Δv_y / V_fin       a lateral gust is an angle-of-attack shift
   *     ΔF_y   = q̄_fin · S · a · Δα   the surface's response to it
   *     ΔN_z   = x_fin · ΔF_y         the moment that force makes about the CG
   *
   * `V_fin` is the axial flow *including the slipstream*, which is the term that makes this work
   * at walking pace: at full power and 5 kt the propwash is already 30 m/s, so the fin has real
   * dynamic pressure and the rudder real authority long before the wing does. A model that used
   * the free-stream speed here would conclude the rudder is useless on the take-off roll, which
   * is the opposite of the truth and the reason it is the rudder that steers a taildragger.
   *
   * P-factor is not re-derived — it is a force on the propeller disc, not on the fin, and the
   * propulsion model already publishes it exactly.
   */
  private updateYawBalance(
    slipstreamAxialMps: number,
    slipstreamSwirlYMps: number,
    pFactorYawNm: number,
    axialSpeedMps: number,
    densityKgPerM3: number,
    rudderRangeDeg: number,
  ): void {
    const balance = this.yawBalance;
    const fin = this.fin;
    if (fin === null) {
      balance.pFactorNm = pFactorYawNm;
      balance.swirlNm = 0;
      balance.finRiggingNm = 0;
      balance.disturbanceNm = pFactorYawNm;
      balance.rudderAuthorityNm = 0;
      return;
    }

    // Axial flow the fin sees. Floored well below flying speed rather than at zero: with the
    // engine stopped and the aeroplane parked there is no authority and no disturbance, and the
    // ratio of two zeros is not a rudder position.
    const finSpeed = Math.max(1, axialSpeedMps + slipstreamAxialMps * fin.slipstreamFraction);
    const finDynamicPressure = 0.5 * densityKgPerM3 * finSpeed * finSpeed;
    const finForcePerRad = finDynamicPressure * fin.areaM2 * fin.liftSlopePerRad;

    // Swirl, as an alpha shift on the fin. The sign chain: a clockwise propeller throws the wake
    // to port at the fin (`slipstreamSwirlYMps` negative), which is a positive α on a panel whose
    // normal points to starboard, which is a force to starboard, which — on a negative arm —
    // yaws the nose left.
    const swirlAlphaRad = -(slipstreamSwirlYMps * fin.slipstreamFraction) / finSpeed;
    balance.swirlNm = fin.armM * finForcePerRad * swirlAlphaRad;
    // …and the rigging, which is the same arithmetic on a fixed angle and pulls the other way.
    balance.finRiggingNm = fin.armM * finForcePerRad * fin.incidenceRad;

    balance.pFactorNm = pFactorYawNm;
    balance.disturbanceNm = balance.pFactorNm + balance.swirlNm + balance.finRiggingNm;

    // A unit of rudder command is the full deflection, through the panel's effectiveness τ.
    balance.rudderAuthorityNm =
      fin.armM * finForcePerRad * fin.rudderGain * rudderRangeDeg * DEG2RAD;
  }

  private updateDamage(state: FlightState, limits: LoadedAircraft['config']['limits']): void {
    const damage = this.damage;
    const indicatedKt = state.indicatedAirspeedMps / 0.514444;

    if (Math.abs(state.loadFactor) > Math.abs(damage.peakLoadFactor)) {
      damage.peakLoadFactor = state.loadFactor;
    }
    if (state.loadFactor > limits.gLimitPositive || state.loadFactor < limits.gLimitNegative) {
      damage.overstressed = true;
    }
    if (indicatedKt > limits.vneKias) damage.overspeed = true;
    if (state.flapAngleDeg > 0.5 && indicatedKt > limits.vfeKias) damage.flapOverspeed = true;

    // Touching the ground is normal; touching it fast, steeply or inverted is not.
    if (state.onGround) {
      const tooFast = this.gearOutput.touchdownRateMps > 4.5;
      const wrongAttitude = Math.abs(state.rollRad) > 25 * DEG2RAD || state.pitchRad < -20 * DEG2RAD;
      if (tooFast || wrongAttitude) damage.crashed = true;
    }

    /*
     * Terrain impact — anything the wheels did not catch.
     *
     * The margin is the reason this is not simply `altitudeM < groundElevationM`: the state's
     * altitude is the *centre of gravity's*, and a parked aeroplane's CG stands one gear leg above
     * the ground it is resting on. Charging it for that would crash every aircraft on the runway.
     * So the surface has to be crossed by more than the undercarriage can account for — and when
     * the gear is up there is nothing to account for, which is why the margin follows the gear.
     *
     * The old fixed 3 m was tuned for the wheels-down case and applied to both, so a belly landing
     * or a gear-up dive into a hillside buried the aeroplane three metres into the rock before the
     * simulator agreed anything had happened. At 500 kt that is a sixtieth of a second, so it was
     * never a detection *failure* — but it was three metres of aeroplane inside the scenery, and
     * the explosion now goes off where the impact is rather than under it.
     */
    const clearanceM = 0.5 + this.gearGroundClearanceM * this.gearFractionValue;
    if (state.altitudeM < this.groundElevationM - clearanceM) damage.crashed = true;
  }

  /** Gear diagnostics from the last tick. */
  get gear(): GearOutput {
    return this.gearOutput;
  }

  /**
   * Interpolate the pose between the last two physics ticks for rendering.
   * Returns reused objects — copy out of them, do not retain them.
   */
  samplePose(alpha: number): {
    positionNed: Vector3;
    attitude: Quaternion;
    propAngleRad: number;
  } {
    this.sampledPositionNed.lerpVectors(this.previousPositionNed, this.body.positionNed, alpha);
    this.sampledAttitude.slerpQuaternions(this.previousAttitude, this.body.attitude, alpha);

    // Interpolate the propeller the short way round so it never spins backwards at the wrap.
    let delta = this.propAngleRad - this.previousPropAngleRad;
    if (delta > Math.PI) delta -= Math.PI * 2;
    else if (delta < -Math.PI) delta += Math.PI * 2;

    /*
     * The wrapper is reused too, not just the vectors inside it.
     *
     * The doc comment above has always said "returns reused objects", and that was true of the
     * position and the attitude and false of the object holding them — a fresh three-field literal
     * every frame, one field of which is a double and so costs a boxed number as well.
     */
    this.sampledPose.propAngleRad = this.previousPropAngleRad + delta * alpha;
    return this.sampledPose;
  }

  /**
   * Snap the interpolation history to the current pose, e.g. after a respawn.
   *
   * **This is a teleport, and it must not be used for a floating-origin rebase** — see
   * {@link applyOriginShift}. Snapping is right when the aeroplane genuinely has no past: a
   * respawn, a scripted rail. It is wrong when the aeroplane is exactly where it was and only the
   * coordinate system moved, because it throws away the interpolation the renderer was mid-way
   * through and the aeroplane visibly skips.
   */
  syncPose(): void {
    this.previousPositionNed.copy(this.body.positionNed);
    this.previousAttitude.copy(this.body.attitude);
    this.previousPropAngleRad = this.propAngleRad;
  }

  /**
   * Carry the interpolation history across a floating-origin rebase.
   *
   * The rebase moves the coordinate system, not the aeroplane: `main.ts` zeroes
   * `body.positionNed` because the aircraft *is* the new origin. Anything holding a position in
   * the old frame has to be translated by the same delta or it is left describing a point 500 m
   * away — which is the defect M7 part 2 found in the camera rig, the fly-by tripod and every
   * round in the air, and fixed for those with the same shape of call.
   *
   * The interpolation history was missed in that sweep, because it is *sim*-frame state and the
   * audit was looking for render-space consumers. It was handled with {@link syncPose}, which
   * discards rather than translates: `previous` and `current` become equal, `samplePose` returns
   * the raw tick pose for every alpha, and the rendered aeroplane skips forward by whatever
   * fraction of a tick the interpolation was carrying. At 120 Hz and 400 kt a tick is 1.7 m, and
   * at the 500 m rebase threshold this happened **every 2.4 seconds** — which is what a pilot
   * reported as "the aircraft visibly jitters sometimes during flight".
   *
   * Attitude and propeller angle are deliberately untouched: a rebase is a pure translation along
   * the surface, so neither is expressed in the moved frame and shifting them would be the bug.
   *
   * @param deltaNed How far the origin moved, in the *old* frame — i.e. what to add to a stored
   *   position to express it in the new one. Pinned by `rebaseInterpolation.test.ts`.
   */
  applyOriginShift(deltaNed: Vector3): void {
    this.previousPositionNed.add(deltaNed);
  }

  /** Set the flap angle directly, bypassing the actuator — for trim and test set-up. */
  setFlapAngleDeg(angleDeg: number): void {
    this.flapAngleDeg = clamp(angleDeg, 0, this.maxFlapDeg);
  }

  get flapAngle(): number {
    return this.flapAngleDeg;
  }

  /**
   * Put the aeroplane back to a clean starting configuration.
   *
   * The gear goes down and the boards close because a respawn puts you on a runway or on final,
   * and the fly-by-wire integrator is cleared because it is carrying the trim state of a flight
   * that is over — left alone it would hand the new one a standing pitch command.
   */
  resetSystems(): void {
    this.gearFractionValue = 1;
    this.speedbrakeFractionValue = 0;
    this.fbw?.reset();
  }

  /** Undercarriage position: 0 up and locked, 1 down and locked. */
  get gearFraction(): number {
    return this.gearFractionValue;
  }

  /** Clear the damage flags, e.g. after a respawn. */
  resetDamage(): void {
    this.damage.overstressed = false;
    this.damage.flapOverspeed = false;
    this.damage.overspeed = false;
    this.damage.crashed = false;
    this.damage.peakLoadFactor = 1;
  }
}
